import { Injectable, inject, effect, signal, computed } from '@angular/core';
import { LoginService } from  './auth/login/login.service';

import { JwtHelperService } from '@auth0/angular-jwt';
import {environment} from '../environments/environment';
import {interval} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthTokenWatchService {
  private jwtHelper = new JwtHelperService();
  private loginService = inject(LoginService);

  readonly idToken = signal<string | null>(localStorage.getItem('id_token'));
  private _exp = signal<number>(this.getStoredExp());
  private tick = signal(0);
  private hasRefreshed = false;

  constructor() {
    // 🔁 Start ticking every 10 seconds to trigger recomputation
    //…is purely being used to trigger recomputation of your computed() signals (secondsLeft and isExpiringSoon)
    // every 10 seconds so that the effect() gets evaluated and you get logging like:
    // this cane be commented out for production
    interval(10000).subscribe(() => {
      this.tick.update(t => t + 1);
    });

    effect(() => {
      const refresh_token = localStorage.getItem('refresh_token');
      const username = localStorage.getItem('username');

      // console.log('⏳ Token seconds left:', this.secondsLeft());
      // console.log('🧠 Expiring soon:', this.isExpiringSoon());

      if (!refresh_token || !username) {
        console.warn('❌ No refresh token or username in localStorage');
        return;
      }

      if (this.isExpiringSoon() && !this.hasRefreshed) {
        this.hasRefreshed = true;
        this.tryRefresh();
      }

      if (this.secondsLeft() > 60) {
        this.hasRefreshed = false;
      }
    });
  }

  readonly secondsLeft = computed(() => {
    this.tick(); // ✅ Dependency to recompute
    const exp = this._exp();
    if (!exp || isNaN(exp)) return 0;
    return Math.max(0, Math.floor(exp - Date.now() / 1000)); // ⛔ no negative seconds
  });

  readonly isExpiringSoon = computed(() =>
    this.secondsLeft() < 60 && this.secondsLeft() > 0
  );

  private getStoredExp(): number {
    const rawExp = localStorage.getItem('exp');
    return rawExp ? parseInt(rawExp, 10) : 0;
  }

  refreshFromStorage() {
    this.idToken.set(localStorage.getItem('id_token'));
    this._exp.set(this.getStoredExp());

    // ✅ Force recompute instantly
    this.tick.update(t => t + 1);
  }

  private tryRefresh() {
    const refresh_token = localStorage.getItem('refresh_token');
    const username = localStorage.getItem('username');
    const url = `${environment.API_AUTH_URL}/refresh`;

    if (refresh_token && username) {
      console.log('🔁 Attempting token refresh');
      this.loginService.refreshToken(url, username, refresh_token).subscribe({
        next: () => {
          console.log('✅ Token refreshed');
          this.refreshFromStorage(); // ✅ Reset countdown
        },
        error: () => {
          console.warn('❌ Refresh failed — logging out');
          this.loginService.logout();
        }
      });
    } else {
      console.warn('⚠️ Missing refresh_token or username');
      this.loginService.logout();
    }
  }
}
